<template>
  <div :class="$style.wrapper">
    <native-close-confirmation
      v-if="hideInProgress"
      @resolve="handleConfirmResolve"
    />
    <div
      v-if="!reference"
      ref="reference"
      :class="$style.referenceWrapper"
      @focusin="handleFocusin"
      @focusout="handleFocusout"
    >
      <slot
        :show="show"
        :hide="tryHide"
        :toggle="toggle"
        :shown="isShown"
        :disabled="disabled"
      />
    </div>
    <component
      :is="layer"
      v-if="isShown"
      :id="id"
      ref="layer"
      tabindex="0"
      :trigger="getReference"
      :content-class="contentClass"
      :content-style="contentStyle"
      :data-ignore-outside-click="!addLayer ? 'true' : undefined"
      :on-hide="handleOnHide"
      @remove="tryHide"
    >
      <slot
        name="content"
        :hide="tryHide"
        :max-height="contentStyle.maxHeight"
      />
    </component>
  </div>
</template>

<script>
import { computePosition, offset, shift, flip, size, autoUpdate } from '@floating-ui/dom';
import { nextTick, defineComponent } from 'vue';
import BaseLayer from '@/shared/legacy/ui/base-layer/base-layer.vue';
import LayerPortal from '@/shared/legacy/ui/layer-portal/layer-portal.vue';
import { flipSizeFixMiddleware } from '@/shared/legacy/ui/base-dropdown/base-popover/flip-size-fix-middleware';
import NativeCloseConfirmation from '../../modal-next/native-close-confirmation.vue';

export default defineComponent({
  name: 'BasePopover',
  components: { NativeCloseConfirmation },
  props: {
    id: {
      type: String,
      default: undefined
    },
    confirmBeforeClose: Boolean,
    shown: Boolean,
    disabled: Boolean,
    placement: {
      type: String,
      default: 'bottom-start'
    },
    distance: {
      type: Number,
      default: 10
    },
    shift: {
      type: Number,
      default: 0
    },
    boundary: {
      type: Element,
      default: undefined
    },
    overflowPadding: {
      type: [Number, Object],
      default: 0
    },
    contentClass: BaseLayer.props.contentClass,
    addLayer: {
      type: Boolean,
      default: true
    },
    flip: {
      type: Boolean,
      default: true
    },
    autoBoundaryMaxSize: {
      type: Boolean,
      default: true
    },
    willChange: {
      type: String,
      default: 'transform'
    },
    maxHeight: {
      type: [String, Number],
      default: Infinity
    },
    strategy: {
      type: String,
      default: 'absolute'
    },
    middlewares: {
      type: Array,
      default: () => []
    },
    preventOverflowOnCrossAxis: {
      type: Boolean,
      default: false
    },
    reference: {
      type: HTMLElement,
      default: null
    }
  },
  emits: ['update:shown', 'shown', 'hidden', 'dispose', 'update', 'focus', 'blur'],
  data() {
    return {
      isShown: false,
      contentStyle: {},
      isFocus: false,
      hideInProgress: false,
      confirmed: false,
      resolvedPlacement: null
    };
  },
  computed: {
    layer() {
      return this.addLayer ? BaseLayer : LayerPortal;
    },
    normalizedMaxHeight() {
      return typeof this.maxHeight === 'string' ? Number.parseInt(this.maxHeight, 10) : this.maxHeight;
    },
    computedMiddlewares() {
      const middlewares = [
        offset({
          mainAxis: this.distance,
          crossAxis: this.shift
        })
      ];
      if (this.flip) {
        middlewares.push(
          flipSizeFixMiddleware,
          flip({
            padding: this.overflowPadding,
            boundary: this.boundary
          })
        );
      }
      middlewares.push(
        shift({
          crossAxis: this.preventOverflowOnCrossAxis ?? false,
          padding: this.overflowPadding,
          boundary: this.boundary
        })
      );
      if (this.autoBoundaryMaxSize) {
        middlewares.push(
          size({
            padding: this.overflowPadding,
            boundary: this.boundary,
            apply: ({ availableHeight, elements }) => {
              const value = `${Math.min(availableHeight, this.normalizedMaxHeight)}px`;
              elements.floating.style.maxHeight = value;
              this.contentStyle.maxHeight = value;
            }
          })
        );
      }
      return middlewares.concat(this.middlewares);
    }
  },
  watch: {
    shown: 'handleShown',
    disabled(value) {
      if (value) {
        this.dispose();
      } else {
        this.init();
      }
    },
    isFocus: {
      immediate: true,
      handler(flag) {
        flag ? this.$emit('focus') : this.$emit('blur');
      }
    }
  },
  mounted() {
    this.init();
  },
  beforeUnmount() {
    this.dispose();
  },
  methods: {
    handleOnHide() {
      if (this.confirmBeforeClose && !this.confirmed) {
        this.hideInProgress = true;
        return false;
      }

      return true;
    },
    handleConfirmResolve(result) {
      this.hideInProgress = false;
      if (result) {
        this.confirmed = true;
        this.hide();
      }
    },
    show(event) {
      if (this.disabled || this.isShown) {
        return;
      }
      this.confirmed = false;
      if (event instanceof Event) {
        event.preventDefault();
      }
      // подписываемся на все события скроллов, т.к. у нас всегда контент выносится в конец боди,
      // при вложении нескольких подобных элементов друг в друга последующие перестают позиционироваться,
      // т.к. находятся не в скролящямся элементе
      window.addEventListener('scroll', this.updatePosition, {
        capture: true,
        passive: true
      });
      window.addEventListener('resize', this.handleWindowResize, { passive: true });

      this.isShown = true;
      nextTick(() => {
        const reference = this.getReference();
        if (!reference) {
          return;
        }
        this.stopAutoUpdate = autoUpdate(reference, this.getContent(), this.updatePosition, {
          ancestorScroll: false
        });
        this.$emit('update:shown', true);
        this.$emit('shown');
      });
    },
    tryHide() {
      if (this.confirmBeforeClose && !this.confirmed) {
        this.$nextTick(() => {
          this.hideInProgress = true;
        });
        return false;
      }
      this.hide();
      return true;
    },
    hide(event) {
      this.hideInProgress = false;
      if (!this.isShown) {
        return;
      }
      if (event instanceof Event) {
        event.preventDefault();
      }
      window.removeEventListener('scroll', this.updatePosition, {
        capture: true,
        passive: true
      });
      window.removeEventListener('resize', this.handleWindowResize, { passive: true });
      this.stopAutoUpdate?.();
      this.isShown = false;
      this.resolvedPlacement = null;
      this.setDefaultContentStyle();
      this.$emit('update:shown', false);
      this.$emit('hidden');
    },
    toggle(event) {
      if (this.isShown) {
        this.tryHide();
      } else {
        this.show(event);
      }
    },
    init() {
      this.setDefaultContentStyle();
      if (this.shown) {
        this.show();
      }
    },
    dispose() {
      this.hide();
      this.$emit('dispose');
    },
    setDefaultContentStyle() {
      this.contentStyle = { maxHeight: `${this.normalizedMaxHeight}px` };
    },
    updatePosition() {
      const reference = this.getReference();
      // reference sits inside a hidden ancestor (e.g. covered by another modal) — its rect is
      // zero, which would compute to (0, 0). Skip and keep the last valid position; autoUpdate's
      // ResizeObserver will call updatePosition again once the ancestor becomes visible.
      if (!reference || reference.getClientRects().length === 0) {
        return Promise.resolve();
      }

      return computePosition(reference, this.getContent(), {
        strategy: this.strategy,
        placement: this.resolvedPlacement || this.placement,
        middleware: this.computedMiddlewares
      }).then((data) => {
        const { x, y, strategy } = data;

        this.resolvedPlacement = data.placement;

        this.contentStyle = {
          ...this.contentStyle,
          position: strategy,
          top: '0',
          left: '0',
          transform: `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0)`,
          /**
           * Свойство willChange настраиваемое, чтобы исправить проблему
           * с размытием контента в конкретных dropdown'ах;
           * Подробная хронология и описание тут:
           * https://huntflow.atlassian.net/browse/SRV-22656
           */
          willChange: this.willChange
        };

        nextTick(() => {
          this.$emit('update');
        });
      });
    },
    handleShown() {
      if (this.shown) {
        this.show();
      } else {
        this.hide();
      }
    },
    handleWindowResize() {
      this.resolvedPlacement = null;
      this.updatePosition();
    },
    getReference() {
      if (this.reference) {
        return this.reference;
      }

      // Походу есть такой кейс:
      // 1) кликается на триггер дропдауна
      // 2) вызывается handleFocusin, который дёргает getReference
      // 3) в это время компонент с дропдауном удаляется и this.$refs.reference становится falsy
      // https://huntflow.atlassian.net/browse/DEV-18415
      const refNodes = this.$refs.reference?.querySelectorAll('*');
      if (!refNodes) {
        return undefined;
      }

      const reference = Array.from(refNodes).find((node) => {
        const display = getComputedStyle(node).display;
        if (display === 'contents' || display === 'none') {
          return false;
        }
        return true;
      });
      if (!reference) {
        throw new Error('reference not found');
      }
      return reference;
    },
    getContent() {
      return this.$refs.layer?.getContent();
    },
    handleFocusin(event) {
      const reference = this.getReference();
      if (!reference) {
        return;
      }
      if (reference.contains(event.target) || this.getContent()?.contains(event.target)) {
        this.isFocus = true;
      }
    },
    handleFocusout(event) {
      const reference = this.getReference();
      if (!reference) {
        return;
      }
      if (
        reference.contains(event.relatedTarget)
        || this.getContent()?.contains(event.relatedTarget)
      ) {
        return;
      }
      this.isFocus = false;
    }
  }
});
</script>

<style module>
.wrapper,
.referenceWrapper {
  display: contents;
}
</style>

<i18n lang="json">{}</i18n>
